import clsMerge from "../util/clsMerge";
import {FCWithHTMLAttributes} from "../types/reactTypes";
import {tv} from "tailwind-variants";

export enum ButtonVariant {
	PRIMARY = "primary",
	SECONDARY = "secondary",
	OUTLINE_DARK = "outline-dark",
	OUTLINE_LIGHT = "outline-light",
	DESTRUCTIVE = "destructive",
	NAVY = "navy",
	GRAY_SECONDARY = "gray-secondary",
	WHITE_BORDERLESS = "white-borderless",
	FILTER_SQUARE = "filter-square",
	FILTER_ROUND = "filter-round",
}

export enum ButtonSizes {
	SMALL = "small",
	LARGE = "large",
}

export const buttonStyles = tv({
	base: "rounded-full text-md text-center",
	variants: {
		size: {
			[ButtonSizes.SMALL]: "px-6 py-2",
			[ButtonSizes.LARGE]: "px-10 py-3",
		},
		variant: {
			[ButtonVariant.PRIMARY]:
				"bg-orange-700 text-gray-100 hover:bg-brick-orange focus-visible:bg-brick-orange disabled:bg-gray-800 uppercase",
			[ButtonVariant.SECONDARY]:
				"bg-gray-900 text-gray-100 hover:bg-gray-800 focus-visible:bg-gray-800 disabled:bg-gray-800 uppercase",
			[ButtonVariant.OUTLINE_DARK]:
				"border border-midtone-blue text-midtone-blue hover:bg-midtone-blue focus-visible:bg-midtone-blue hover:text-gray-100 focus-visible:text-gray-100 disabled:bg-gray-200  disabled:text-gray-700 uppercase",
			[ButtonVariant.OUTLINE_LIGHT]:
				"border border-gray-100 text-gray-100 hover:border-gray-200 hover:bg-gray-100 focus-visible:bg-gray-100 focus-visible:border-gray-100 hover:text-gray-900 focus-visible:text-gray-900 disabled:bg-gray-200 disabled:text-gray-700 uppercase",
			[ButtonVariant.DESTRUCTIVE]:
				"border border-dark-orange text-dark-orange hover:bg-gray-400 focus-visible:bg-gray-400 uppercase",
			[ButtonVariant.NAVY]:
				"bg-midtone-blue text-gray-100 hover:bg-navy-900 focus-visible:bg-navy-900 disabled:bg-gray-800 disabled:text-gray-500 uppercase",
			[ButtonVariant.GRAY_SECONDARY]:
				"bg-gray-200 text-gray-800 hover:bg-gray-400 focus-visible:bg-gray-400 disabled:bg-gray-100 disabled:text-gray-600 uppercase",
			[ButtonVariant.WHITE_BORDERLESS]:
				"bg-white text-gray-800 hover:bg-gray-100 disabled:bg-gray-200 disabled:text-gray-500",
			[ButtonVariant.FILTER_SQUARE]:
				"rounded border border-gray-300 px-4 py-2 text-sm bg-gray-100 normal-case",
			[ButtonVariant.FILTER_ROUND]:
				"md:px-4 md:py-1 text-sm bg-white text-nowrap px-6 py-3",
		},
		selected: {
			true: "",
		},
	},
	compoundVariants: [
		{
			variant: ButtonVariant.FILTER_SQUARE,
			selected: false,
			className:
				"hover:bg-gray-200 active:bg-gray-900 active:text-gray-200 active:hover:bg-gray-900",
		},
		{
			variant: ButtonVariant.FILTER_SQUARE,
			selected: true,
			className: "bg-gray-900 text-gray-200",
		},
		{
			variant: ButtonVariant.FILTER_ROUND,
			selected: false,
			className:
				"border hover:bg-gray-200 hover:border-gray-700 active:bg-gray-900 active:border-gray-900 active:text-gray-200 active:hover:bg-gray-900 active:hover:border-gray-900 normal-case",
		},
		{
			variant: ButtonVariant.FILTER_ROUND,
			selected: true,
			className: "bg-gray-900 text-gray-200",
		},
	],
});

export interface ButtonVariantProps {
	variant?: ButtonVariant;
	size?: ButtonSizes;
	selected?: boolean;
}

type ButtonProps = ButtonVariantProps &
	React.ButtonHTMLAttributes<HTMLButtonElement>;

/**
 * Standard <button> component.
 *
 * See LinkButton if you want an <a> or RACButton for RAC controls.
 */
const Button: FCWithHTMLAttributes<ButtonProps> = ({
	children,
	className = "",
	variant = ButtonVariant.PRIMARY,
	size = ButtonSizes.LARGE,
	selected = false,
	...props
}) => {
	return (
		<button
			className={clsMerge(buttonStyles({variant, size, selected}), className)}
			{...props}
		>
			{children}
		</button>
	);
};

export default Button;
